import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  DatePicker,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Pagination,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  BankOutlined,
  CloudUploadOutlined,
  CompressOutlined,
  DatabaseOutlined,
  BookOutlined,
  CommentOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExpandOutlined,
  EyeOutlined,
  FileOutlined,
  FilterOutlined,
  FolderOpenOutlined,
  SearchOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment';
import {
  useDeleteTeamAssistantDocument,
  useTeamAssistantConversationFilters,
  useTeamAssistantDocuments,
  useTeamAssistantKnowledgeOverview,
  useTeamAssistantKnowledgeSearch,
} from '../../hooks/useTeamAssistantQueries';
import assistant from '../../services/assistant';
import { formatBytes } from '../../utils/formatBytes';
import { TEAM_ASSISTANT_OVERLAY_Z_INDEX } from '../../utils/teamAssistantZIndex';
import TeamAssistantDocumentPreviewModal from './TeamAssistantDocumentPreviewModal';
import TeamAssistantMemoriesPanel from './TeamAssistantMemoriesPanel';
import './team-assistant-knowledge.css';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const defaultDocFilters = () => ({
  q: '',
  userId: undefined,
  dates: null,
  indexed: undefined,
  searchMode: undefined,
  sort: 'newest',
});

const defaultSearchFilters = () => ({
  q: '',
  type: 'all',
  userId: undefined,
  dates: null,
});

function fileExt(name) {
  const i = (name || '').lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toUpperCase() : 'FILE';
}

function countDocFilters(f) {
  let n = 0;
  if ((f.q || '').trim()) n += 1;
  if (f.userId != null) n += 1;
  if (f.dates?.[0] || f.dates?.[1]) n += 1;
  if (f.indexed) n += 1;
  if (f.searchMode) n += 1;
  if (f.sort && f.sort !== 'newest') n += 1;
  return n;
}

const ModePill = ({ mode, compact = false }) => {
  const cls = `team-assistant-kb-mode-pill team-assistant-kb-mode-pill--${mode || 'stored'}`;
  const label = compact
    ? mode === 'indexed'
      ? 'Search'
      : mode === 'vision'
        ? 'Vision'
        : 'Stored'
    : mode === 'indexed'
      ? 'File search'
      : mode === 'vision'
        ? 'Vision'
        : 'Stored';
  return <span className={cls}>{label}</span>;
};

const MetricCard = ({ icon, label, value, tone }) => (
  <div className={`team-assistant-kb-metric team-assistant-kb-metric--${tone}`}>
    <div className="team-assistant-kb-metric-label">{icon} {label}</div>
    <div className="team-assistant-kb-metric-value">{value}</div>
  </div>
);

const KnowledgeBaseIntro = () => (
  <div className="team-assistant-kb-intro">
    <span className="team-assistant-kb-intro-title">What is the Knowledge base?</span>
    Everything here helps the Team Assistant answer insurance and Market Finder questions for your whole
    staff — not a personal ChatGPT thread. Uploads, company catalog data, searchable chat history, and
    confirmed team facts are combined when someone asks about carriers, lines of business, commissions,
    underwriting, or internal process.
    <ul className="team-assistant-kb-intro-list">
      <li>
        <strong>Overview</strong> — See whether uploads, search index, catalog, and memories are ready.
      </li>
      <li>
        <strong>Files</strong> — Team PDFs and Office docs (guides, appointments, rate sheets) the assistant
        can search.
      </li>
      <li>
        <strong>Deep search</strong> — Find past staff questions and replies (e.g. a carrier or state).
      </li>
      <li>
        <strong>Team memory</strong> — Pin vetted facts (admin-confirmed) so every reply includes them.
      </li>
    </ul>
  </div>
);

const TeamAssistantKnowledgeDrawer = ({ open, onClose, currentUser }) => {
  const [docFilters, setDocFilters] = useState(defaultDocFilters);
  const [searchFilters, setSearchFilters] = useState(defaultSearchFilters);
  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [fullscreen, setFullscreen] = useState(true);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [filesPage, setFilesPage] = useState(1);
  const [filesPageSize, setFilesPageSize] = useState(10);

  useEffect(() => {
    if (open) {
      setFullscreen(true);
    }
  }, [open]);

  const overviewQuery = useTeamAssistantKnowledgeOverview({ enabled: open });
  const staffQuery = useTeamAssistantConversationFilters({ enabled: open });
  const deleteMutation = useDeleteTeamAssistantDocument();

  const docApiFilters = useMemo(() => {
    const [from, to] = docFilters.dates || [];
    return {
      q: docFilters.q,
      userId: docFilters.userId,
      from: from ? dayjs(from).format('YYYY-MM-DD') : null,
      to: to ? dayjs(to).format('YYYY-MM-DD') : null,
      indexed: docFilters.indexed,
      searchMode: docFilters.searchMode,
      sort: docFilters.sort,
    };
  }, [docFilters]);

  useEffect(() => {
    setFilesPage(1);
  }, [docApiFilters]);

  const documentsQuery = useTeamAssistantDocuments(docApiFilters, {
    enabled: open && activeTab === 'files',
  });

  const searchApiFilters = useMemo(() => {
    const [from, to] = searchFilters.dates || [];
    return {
      q: searchFilters.q,
      type: searchFilters.type,
      userId: searchFilters.userId,
      from: from ? dayjs(from).format('YYYY-MM-DD') : null,
      to: to ? dayjs(to).format('YYYY-MM-DD') : null,
    };
  }, [searchFilters]);

  const searchQuery = useTeamAssistantKnowledgeSearch(searchApiFilters, {
    enabled: open && activeTab === 'search' && searchSubmitted,
  });

  const staffOptions = (staffQuery.data?.staff || []).map((s) => ({
    value: s.user?.id,
    label: s.user?.name || `User ${s.user?.id}`,
  }));

  const overview = overviewQuery.data;
  const docFilterCount = countDocFilters(docFilters);
  const fileList = documentsQuery.data || [];

  const pagedFiles = useMemo(() => {
    const start = (filesPage - 1) * filesPageSize;
    return fileList.slice(start, start + filesPageSize);
  }, [fileList, filesPage, filesPageSize]);

  const healthScore = useMemo(() => {
    if (!overview) return 0;
    let score = 0;
    const total = overview.documents?.total ?? 0;
    const indexed = overview.documents?.indexed_for_search ?? 0;
    const companies = overview.catalog?.companies ?? 0;
    if (total > 0) score += 25;
    if (indexed > 0) score += 35;
    if (companies > 0) score += 25;
    if ((overview.gaps?.length ?? 0) === 0) score += 15;
    else score += Math.max(0, 15 - overview.gaps.length * 4);
    return Math.min(100, score);
  }, [overview]);

  const handleDelete = async (row) => {
    try {
      await deleteMutation.mutateAsync(row.id);
      message.success(`Removed “${row.original_name}” from the knowledge base`);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Could not delete file');
    }
  };

  const renderFileRow = (row) => (
    <button
      type="button"
      className="team-assistant-kb-file-cell team-assistant-kb-file-cell--clickable"
      onClick={() => setPreviewDoc(row)}
      title="Preview or download"
    >
      <div className="team-assistant-kb-file-icon">
        <FileOutlined />
      </div>
      <div className="team-assistant-kb-file-meta">
        <div className="team-assistant-kb-file-name">{row.original_name}</div>
        <div className="team-assistant-kb-file-sub">
          {fileExt(row.original_name)} · {formatBytes(row.size)}
          {row.created_at ? ` · ${moment(row.created_at).format('MMM D, YY')}` : ''}
          {row.user?.name ? ` · ${row.user.name}` : ''}
        </div>
      </div>
    </button>
  );

  const renderFileActions = (row) => (
    <Space size={0} className="team-assistant-kb-file-actions" onClick={(e) => e.stopPropagation()}>
      <Tooltip title="Preview">
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          aria-label={`Preview ${row.original_name}`}
          onClick={() => setPreviewDoc(row)}
        />
      </Tooltip>
      <Tooltip title="Download">
        <Button
          type="text"
          size="small"
          icon={<DownloadOutlined />}
          aria-label={`Download ${row.original_name}`}
          onClick={async () => {
            try {
              const blob = await assistant.fetchDocumentFile(row.id, { inline: false });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = row.original_name;
              a.click();
              URL.revokeObjectURL(url);
            } catch (e) {
              message.error(e?.message || 'Download failed');
            }
          }}
        />
      </Tooltip>
      <Popconfirm
        title="Remove this file?"
        description="It will be deleted from team storage and removed from search index."
        okText="Delete"
        okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
        onConfirm={() => handleDelete(row)}
      >
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          aria-label={`Delete ${row.original_name}`}
        />
      </Popconfirm>
    </Space>
  );

  const overviewTab = (
    <Spin spinning={overviewQuery.isLoading}>
      <div className="team-assistant-kb-tab-intro">
        <strong>Overview</strong> shows how ready the assistant is for insurance work: team document
        uploads, indexed file search, live Market Finder catalog (companies, LOB, states), and chat memory
        stats. Fix any items listed under <strong>Gaps to address</strong> so answers stay grounded in your
        data.
      </div>
      {overview && (
        <>
          <div className="team-assistant-kb-health">
            <Text strong style={{ fontSize: 13 }}>Knowledge health</Text>
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
              {healthScore}% — uploads, search index, and catalog coverage
            </Text>
            <div className="team-assistant-kb-health-bar">
              <div className="team-assistant-kb-health-fill" style={{ width: `${healthScore}%` }} />
            </div>
          </div>

          <div className="team-assistant-kb-section-title">Team uploads</div>
          <p className="team-assistant-kb-section-blurb">
            Files attached in chat or uploaded for the team. Indexed items are searched when staff ask about
            underwriting rules, forms, or internal docs.
          </p>
          <div className="team-assistant-kb-metric-grid team-assistant-kb-metric-grid--triple">
            <MetricCard
              icon={<FolderOpenOutlined />}
              label="All files"
              value={overview.documents?.total ?? 0}
              tone="files"
            />
            <MetricCard
              icon={<SearchOutlined />}
              label="Search index"
              value={overview.documents?.indexed_for_search ?? 0}
              tone="files"
            />
            <MetricCard
              icon={<EyeOutlined />}
              label="Vision"
              value={overview.documents?.vision_only ?? 0}
              tone="files"
            />
          </div>

          <div className="team-assistant-kb-section-title">Market Finder catalog</div>
          <p className="team-assistant-kb-section-blurb">
            Same carrier database as the website — companies, lines of business, and states the assistant
            uses for market and appointment questions.
          </p>
          <div className="team-assistant-kb-metric-grid team-assistant-kb-metric-grid--triple">
            <MetricCard
              icon={<ShopOutlined />}
              label="Companies"
              value={overview.catalog?.companies ?? 0}
              tone="catalog"
            />
            <MetricCard
              icon={<BankOutlined />}
              label="LOB"
              value={overview.catalog?.lines_of_business ?? 0}
              tone="catalog"
            />
            <MetricCard
              icon={<DatabaseOutlined />}
              label="States"
              value={overview.catalog?.states ?? 0}
              tone="catalog"
            />
          </div>

          {overview.chat_memory && (
            <>
              <div className="team-assistant-kb-section-title">Team chat memory</div>
              <p className="team-assistant-kb-section-blurb">
                Past team chat is indexed for semantic search; confirmed facts from the Team memory tab are
                always included in new replies.
              </p>
              <div className="team-assistant-kb-metric-grid team-assistant-kb-metric-grid--triple">
                <MetricCard
                  icon={<CommentOutlined />}
                  label="Chat indexed"
                  value={`${overview.chat_memory.embedding_count ?? 0} / ${overview.chat_memory.message_count ?? 0}`}
                  tone="files"
                />
                <MetricCard
                  icon={<BookOutlined />}
                  label="Confirmed facts"
                  value={overview.chat_memory.confirmed_memories ?? 0}
                  tone="catalog"
                />
                <MetricCard
                  icon={<BookOutlined />}
                  label="Pending review"
                  value={overview.chat_memory.candidate_memories ?? 0}
                  tone="catalog"
                />
              </div>
            </>
          )}

          {overview.gaps?.length > 0 && (
            <>
              <div className="team-assistant-kb-section-title">Gaps to address</div>
              <div className="team-assistant-kb-gap-list">
                {overview.gaps.map((g) => (
                  <div key={g} className="team-assistant-kb-gap-item">{g}</div>
                ))}
              </div>
            </>
          )}

          {overview.recent_uploads?.length > 0 && (
            <>
              <div className="team-assistant-kb-section-title">Recent uploads</div>
              <div className="team-assistant-kb-recent">
                {overview.recent_uploads.map((item) => (
                  <div key={item.id} className="team-assistant-kb-recent-row">
                    <ModePill mode={item.search_mode} />
                    <span className="team-assistant-kb-recent-name" title={item.original_name}>
                      {item.original_name}
                    </span>
                    <Text type="secondary">{item.user?.name}</Text>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </Spin>
  );

  const clearDocFilters = () => setDocFilters(defaultDocFilters());

  const filesTab = (
    <>
      <div className="team-assistant-kb-tab-intro">
        <strong>Files</strong> lists everything the team has uploaded (from chat attachments or admin
        uploads). <strong>File search</strong> means the assistant can read and cite the document;
        <strong> Vision</strong> applies to images in the message you sent;
        <strong> Stored</strong> keeps the file on disk but does not add it to automatic search. Use filters
        to audit who uploaded carrier guides, appointment letters, or rate sheets.
      </div>
      <div className="team-assistant-kb-filter-panel">
        <div className="team-assistant-kb-filter-panel-head">
          <h4>
            <FilterOutlined /> Advanced filters
            {docFilterCount > 0 && (
              <Badge count={docFilterCount} size="small" style={{ marginLeft: 8 }} />
            )}
          </h4>
          {docFilterCount > 0 && (
            <Button type="link" size="small" onClick={clearDocFilters}>Reset</Button>
          )}
        </div>
        <div className="team-assistant-kb-files-filter">
          <div className="team-assistant-kb-files-filter-row">
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="e.g. underwriting, appointment, rate sheet…"
              value={docFilters.q}
              onChange={(e) => setDocFilters((p) => ({ ...p, q: e.target.value }))}
            />
            <Select
              allowClear
              placeholder="Uploader"
              options={staffOptions}
              value={docFilters.userId}
              onChange={(userId) => setDocFilters((p) => ({ ...p, userId }))}
            />
            <Select
              allowClear
              placeholder="KB role"
              value={docFilters.searchMode}
              onChange={(searchMode) => setDocFilters((p) => ({ ...p, searchMode }))}
              options={[
                { value: 'indexed', label: 'File search index' },
                { value: 'vision', label: 'Vision images' },
                { value: 'stored', label: 'Stored only' },
              ]}
            />
          </div>
          <div className="team-assistant-kb-files-filter-row team-assistant-kb-files-filter-row--secondary">
            <Select
              value={docFilters.sort}
              onChange={(sort) => setDocFilters((p) => ({ ...p, sort }))}
              options={[
                { value: 'newest', label: 'Newest first' },
                { value: 'oldest', label: 'Oldest first' },
                { value: 'name', label: 'Name A–Z' },
                { value: 'size', label: 'Largest first' },
              ]}
            />
            <RangePicker
              className="team-assistant-kb-files-filter-dates"
              value={docFilters.dates}
              onChange={(dates) => setDocFilters((p) => ({ ...p, dates }))}
              allowEmpty={[true, true]}
            />
          </div>
        </div>
      </div>

      <div className="team-assistant-kb-files-meta">
        <span>
          <strong>{fileList.length}</strong> file{fileList.length === 1 ? '' : 's'} shown
          {documentsQuery.isFetching ? ' · updating…' : ''}
        </span>
        <Button type="link" size="small" onClick={() => documentsQuery.refetch()}>
          Refresh
        </Button>
      </div>

      <div className="team-assistant-kb-file-list">
        <div className="team-assistant-kb-file-list-head">
          <div className="team-assistant-kb-file-list-col team-assistant-kb-file-list-col--file">File</div>
          <div className="team-assistant-kb-file-list-col team-assistant-kb-file-list-col--role">Search use</div>
          <div className="team-assistant-kb-file-list-col team-assistant-kb-file-list-col--actions" />
        </div>
        <Spin spinning={documentsQuery.isPending}>
          {!documentsQuery.isPending && fileList.length === 0 ? (
            <div className="team-assistant-kb-file-list-empty">
              <Empty description="No files match — upload PDFs or Office docs from the chat composer" />
            </div>
          ) : (
            <div className="team-assistant-kb-file-list-body">
              {pagedFiles.map((row) => (
                <div key={row.id} className="team-assistant-kb-file-list-row">
                  <div className="team-assistant-kb-file-list-col team-assistant-kb-file-list-col--file">
                    {renderFileRow(row)}
                  </div>
                  <div className="team-assistant-kb-file-list-col team-assistant-kb-file-list-col--role">
                    <ModePill mode={row.search_mode} compact />
                  </div>
                  <div className="team-assistant-kb-file-list-col team-assistant-kb-file-list-col--actions">
                    {renderFileActions(row)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Spin>
        {fileList.length > 0 && (
          <div className="team-assistant-kb-file-list-foot">
            <Pagination
              size="small"
              current={filesPage}
              pageSize={filesPageSize}
              total={fileList.length}
              showSizeChanger
              pageSizeOptions={[10, 20, 50]}
              onChange={(page, size) => {
                setFilesPage(page);
                setFilesPageSize(size);
              }}
              showTotal={(total, range) => `${range[0]}–${range[1]} of ${total}`}
            />
          </div>
        )}
      </div>
    </>
  );

  const runSearch = () => setSearchSubmitted(true);

  const searchTab = (
    <>
      <div className="team-assistant-kb-tab-intro">
        <strong>Deep search</strong> is for people — not the AI. Search past team chat and file metadata by
        keyword (carrier name, state, LOB, commission, etc.), message type, staff member, or date. Results
        help you audit what was asked before; the assistant also uses semantic search on older chat
        automatically when replying.
      </div>
      <div className="team-assistant-kb-search-panel">
        <div className="team-assistant-kb-deep-search-toolbar">
          <Input
            className="team-assistant-kb-deep-search-keyword"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="e.g. XYZ Insurance, workers comp, Texas…"
            value={searchFilters.q}
            onChange={(e) => {
              setSearchSubmitted(false);
              setSearchFilters((p) => ({ ...p, q: e.target.value }));
            }}
            onPressEnter={runSearch}
          />
          <Select
            className="team-assistant-kb-deep-search-type"
            value={searchFilters.type}
            onChange={(type) => {
              setSearchSubmitted(false);
              setSearchFilters((p) => ({ ...p, type }));
            }}
            options={[
              { value: 'all', label: 'All types' },
              { value: 'document', label: 'Files' },
              { value: 'question', label: 'Questions' },
              { value: 'assistant_reply', label: 'Replies' },
            ]}
          />
          <Select
            className="team-assistant-kb-deep-search-user"
            allowClear
            placeholder="Team member"
            options={staffOptions}
            value={searchFilters.userId}
            onChange={(userId) => {
              setSearchSubmitted(false);
              setSearchFilters((p) => ({ ...p, userId }));
            }}
          />
          <RangePicker
            className="team-assistant-kb-deep-search-dates"
            value={searchFilters.dates}
            onChange={(dates) => {
              setSearchSubmitted(false);
              setSearchFilters((p) => ({ ...p, dates }));
            }}
            allowEmpty={[true, true]}
          />
          <Button
            type="primary"
            className="team-assistant-kb-deep-search-btn"
            icon={<SearchOutlined />}
            onClick={runSearch}
          >
            Search
          </Button>
        </div>
      </div>

      {searchSubmitted ? (
        <Spin spinning={searchQuery.isPending}>
          {(searchQuery.data || []).length === 0 && !searchQuery.isFetching ? (
            <Empty description="No matches" />
          ) : (
            <div className="team-assistant-kb-search-results">
              {(searchQuery.data || []).map((item) => (
                <div key={item.id} className="team-assistant-kb-result-card">
                  <div className="team-assistant-kb-result-top">
                    <Tag color={item.kind === 'document' ? 'blue' : item.kind === 'question' ? 'cyan' : 'green'}>
                      {item.kind === 'document' ? 'File' : item.kind === 'question' ? 'Question' : 'Reply'}
                    </Tag>
                    <span className="team-assistant-kb-result-title">{item.title}</span>
                    {item.at && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {moment(item.at).format('MMM D, YYYY · h:mm A')}
                      </Text>
                    )}
                  </div>
                  {item.user?.name && (
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                      {item.user.name}
                    </Text>
                  )}
                  <div className="team-assistant-kb-result-snippet">{item.snippet || item.content}</div>
                </div>
              ))}
            </div>
          )}
        </Spin>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Enter a keyword and Search — e.g. a carrier name or line of business"
        />
      )}
    </>
  );

  const modalClass = [
    'team-assistant-knowledge-modal',
    fullscreen ? 'team-assistant-knowledge-modal--fullscreen' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const wrapClass = [
    'team-assistant-knowledge-modal-wrap',
    fullscreen ? 'team-assistant-knowledge-modal-wrap--fullscreen' : '',
  ]
    .filter(Boolean)
    .join(' ');

  /** Above `.team-assistant-workspace--fullscreen` (1200) and floating chat (1100). */
  const knowledgeModalZIndex = TEAM_ASSISTANT_OVERLAY_Z_INDEX;

  return (
    <>
    <TeamAssistantDocumentPreviewModal
      document={previewDoc}
      open={Boolean(previewDoc)}
      onClose={() => setPreviewDoc(null)}
    />
    <Modal
      title={
        <div className="team-assistant-kb-modal-title-row">
          <div className="team-assistant-kb-head">
            <div className="team-assistant-kb-head-icon">
              <CloudUploadOutlined />
            </div>
            <div className="team-assistant-kb-head-text">
              <h3>Knowledge base</h3>
              <p>Insurance team uploads, Market Finder catalog, chat search, and confirmed facts</p>
            </div>
          </div>
          <Space className="team-assistant-kb-modal-actions" size={4}>
            <Tooltip title={fullscreen ? 'Exit full screen' : 'Full screen'}>
              <Button
                type="text"
                icon={fullscreen ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={() => setFullscreen((v) => !v)}
                aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
              />
            </Tooltip>
          </Space>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      centered={!fullscreen}
      width={fullscreen ? '100%' : 920}
      destroyOnClose
      zIndex={knowledgeModalZIndex}
      className={modalClass}
      wrapClassName={wrapClass}
      styles={{
        header: { marginBottom: 0 },
        body: {
          padding: 0,
          maxHeight: fullscreen ? 'calc(100vh - 108px)' : 'min(72vh, 720px)',
          overflow: 'auto',
        },
      }}
    >
      <div className="team-assistant-kb-shell">
        <KnowledgeBaseIntro />
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="team-assistant-kb-tabs"
          items={[
            { key: 'overview', label: 'Overview', children: overviewTab },
            {
              key: 'files',
              label: (
                <span>
                  Files
                  {overview?.documents?.total != null && (
                    <Badge
                      count={overview.documents.total}
                      style={{ marginLeft: 6 }}
                      overflowCount={999}
                      size="small"
                    />
                  )}
                </span>
              ),
              children: filesTab,
            },
            { key: 'search', label: 'Deep search', children: searchTab },
            {
              key: 'memory',
              label: 'Team memory',
              children: (
                <TeamAssistantMemoriesPanel
                  currentUser={currentUser}
                  enabled={open && activeTab === 'memory'}
                />
              ),
            },
          ]}
        />
      </div>
    </Modal>
    </>
  );
};

export default TeamAssistantKnowledgeDrawer;
